// src/pages/DuplicateManagementPage.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Database, AlertTriangle, Music, Users, FileText } from 'lucide-react';
import Navigation from '../components/Navigation/Navigation';
import apiClient from '../utils/apiClient';
import './DuplicateManagementPage.css';

interface DuplicatesData {
  songs: any[][];
  composers: any[][];
  tunes: any[][];
  summary: {
    totalSongDuplicates: number;
    totalComposerDuplicates: number;
    totalTuneDuplicates: number;
    totalDuplicates: number;
  };
}

const DuplicateManagementPage: React.FC = () => {
  const [duplicates, setDuplicates] = useState<DuplicatesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'songs' | 'composers' | 'tunes'>('songs');
  const [merging, setMerging] = useState<string | null>(null);
  const [selectedKeepIds, setSelectedKeepIds] = useState<Record<string, number>>({});
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
  const [showMergeAllModal, setShowMergeAllModal] = useState(false);
  const [mergeAllInProgress, setMergeAllInProgress] = useState(false);

  useEffect(() => {
    loadDuplicates();
  }, []);

  const loadDuplicates = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.get('/duplicates');
      setDuplicates(response.data.data);
    } catch (err) {
      console.error('Error loading duplicates:', err);
      setError('Failed to load duplicate data');
    } finally {
      setLoading(false);
    }
  };

  const handleMergeSongs = async (keepId: number, deleteId: number) => {
    try {
      setMerging(`song-${keepId}-${deleteId}`);
      
      const response = await apiClient.post('/duplicates/songs/merge', {
        keepId,
        deleteId
      });
      
      if (response.data.success) {
        // Reload duplicates after successful merge
        await loadDuplicates();
      }
    } catch (err) {
      console.error('Error merging songs:', err);
      alert('Failed to merge songs. Please try again.');
    } finally {
      setMerging(null);
    }
  };

  const handleMergeComposers = async (keepId: number, deleteId: number) => {
    try {
      setMerging(`composer-${keepId}-${deleteId}`);
      
      const response = await apiClient.post('/duplicates/composers/merge', {
        keepId,
        deleteId
      });
      
      if (response.data.success) {
        // Reload duplicates after successful merge
        await loadDuplicates();
      }
    } catch (err) {
      console.error('Error merging composers:', err);
      alert('Failed to merge composers. Please try again.');
    } finally {
      setMerging(null);
    }
  };

  const handleMergeTunes = async (keepId: number, deleteId: number) => {
    try {
      setMerging(`tune-${keepId}-${deleteId}`);
      
      const response = await apiClient.post('/duplicates/tunes/merge', {
        keepId,
        deleteId
      });
      
      if (response.data.success) {
        // Reload duplicates after successful merge
        await loadDuplicates();
      }
    } catch (err) {
      console.error('Error merging tunes:', err);
      alert('Failed to merge tunes. Please try again.');
    } finally {
      setMerging(null);
    }
  };

  const handleMergeAll = async () => {
    if (selectedGroups.size === 0) {
      alert('Please select at least one group to merge.');
      return;
    }

    setMergeAllInProgress(true);
    try {
      const promises: Promise<any>[] = [];
      
      selectedGroups.forEach(groupKey => {
        const [type, , groupIndex] = groupKey.split('-');
        const index = parseInt(groupIndex);
        
        if (type === 'song' && duplicates?.songs[index]) {
          const group = duplicates.songs[index];
          const selectedKeepId = selectedKeepIds[groupKey] || group[0].song_id;
          const toDelete = group.filter(s => s.song_id !== selectedKeepId);
          
          toDelete.forEach(song => {
            promises.push(
              apiClient.post('/duplicates/songs/merge', {
                keepId: selectedKeepId,
                deleteId: song.song_id
              })
            );
          });
        } else if (type === 'composer' && duplicates?.composers[index]) {
          const group = duplicates.composers[index];
          const selectedKeepId = selectedKeepIds[groupKey] || group[0].composer_id;
          const toDelete = group.filter(c => c.composer_id !== selectedKeepId);
          
          toDelete.forEach(composer => {
            promises.push(
              apiClient.post('/duplicates/composers/merge', {
                keepId: selectedKeepId,
                deleteId: composer.composer_id
              })
            );
          });
        } else if (type === 'tune' && duplicates?.tunes[index]) {
          const group = duplicates.tunes[index];
          const selectedKeepId = selectedKeepIds[groupKey] || group[0].tune_id;
          const toDelete = group.filter(t => t.tune_id !== selectedKeepId);
          
          toDelete.forEach(tune => {
            promises.push(
              apiClient.post('/duplicates/tunes/merge', {
                keepId: selectedKeepId,
                deleteId: tune.tune_id
              })
            );
          });
        }
      });

      await Promise.all(promises);
      await loadDuplicates();
      setSelectedGroups(new Set());
      setShowMergeAllModal(false);
    } catch (err) {
      console.error('Error during merge all:', err);
      alert('Some merges failed. Please check the console and try again.');
    } finally {
      setMergeAllInProgress(false);
    }
  };

  const getSelectedGroupsCount = () => {
    let totalCount = 0;
    selectedGroups.forEach(groupKey => {
      const [type, , groupIndex] = groupKey.split('-');
      const index = parseInt(groupIndex);
      
      if (type === 'song' && duplicates?.songs[index]) {
        const group = duplicates.songs[index];
        const selectedKeepId = selectedKeepIds[groupKey] || group[0].song_id;
        totalCount += group.filter(s => s.song_id !== selectedKeepId).length;
      } else if (type === 'composer' && duplicates?.composers[index]) {
        const group = duplicates.composers[index];
        const selectedKeepId = selectedKeepIds[groupKey] || group[0].composer_id;
        totalCount += group.filter(c => c.composer_id !== selectedKeepId).length;
      } else if (type === 'tune' && duplicates?.tunes[index]) {
        const group = duplicates.tunes[index];
        const selectedKeepId = selectedKeepIds[groupKey] || group[0].tune_id;
        totalCount += group.filter(t => t.tune_id !== selectedKeepId).length;
      }
    });
    return totalCount;
  };

  const renderSongDuplicates = (songGroups: any[][]) => {
    if (songGroups.length === 0) {
      return <div className="text-muted text-center py-4">No song duplicates found</div>;
    }

    return songGroups.map((group, groupIndex) => {
      const groupKey = `song-group-${groupIndex}`;
      const selectedKeepId = selectedKeepIds[groupKey] || group[0].song_id;
      
      return (
        <div key={groupIndex} className="duplicate-group mb-4">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <input
                  type="checkbox"
                  className="form-check-input me-3"
                  checked={selectedGroups.has(groupKey)}
                  onChange={(e) => {
                    const newSelected = new Set(selectedGroups);
                    if (e.target.checked) {
                      newSelected.add(groupKey);
                    } else {
                      newSelected.delete(groupKey);
                    }
                    setSelectedGroups(newSelected);
                  }}
                />
                <h6 className="mb-0">
                  <AlertTriangle size={16} className="me-2 text-warning" />
                  Potential Duplicates ({group.length} songs)
                </h6>
              </div>
              <button 
                className="btn btn-sm btn-primary"
                onClick={() => {
                  const toDelete = group.filter(s => s.song_id !== selectedKeepId);
                  if (toDelete.length > 0 && window.confirm(`This will merge ${toDelete.length} duplicate(s) into the selected song. Continue?`)) {
                    toDelete.forEach(song => {
                      handleMergeSongs(selectedKeepId, song.song_id);
                    });
                  }
                }}
                disabled={merging !== null}
              >
                {merging?.startsWith('song-') ? 'Merging...' : 'Merge Selected'}
              </button>
            </div>
            <div className="card-body">
              {group.map((song, songIndex) => (
                <div key={song.song_id} className="duplicate-item">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <input
                        type="radio"
                        name={groupKey}
                        value={song.song_id}
                        checked={selectedKeepId === song.song_id}
                        onChange={() => setSelectedKeepIds(prev => ({ ...prev, [groupKey]: song.song_id }))}
                        className="me-3"
                      />
                      <div>
                        <strong>{song.title}</strong>
                        <div className="text-muted small">
                          ID: {song.song_id} | 
                          Composer ID: {song.composer_id || 'None'} | 
                          Lyricist ID: {song.lyricist_id || 'None'}
                        </div>
                        <div className="text-muted small">
                          <strong>Tags:</strong> {song.tags?.length > 0 ? song.tags.join(', ') : 'None'}
                        </div>
                        <div className="text-muted small">
                          <strong>Tune ID:</strong> {song.tune_id || 'None'}
                        </div>
                        <div className="text-muted small">
                          <strong>Video Link:</strong> {song.video_link ? <a href={song.video_link} target="_blank" rel="noopener noreferrer">View</a> : 'None'}
                        </div>
                        <div className="text-muted small">
                          <strong>Copyright:</strong> {song.copyright || 'None'}
                        </div>
                      </div>
                    </div>
                  </div>
                  {songIndex < group.length - 1 && <hr />}
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    });
  };

  const renderComposerDuplicates = (composerGroups: any[][]) => {
    if (composerGroups.length === 0) {
      return <div className="text-muted text-center py-4">No composer duplicates found</div>;
    }

    return composerGroups.map((group, groupIndex) => {
      const groupKey = `composer-group-${groupIndex}`;
      const selectedKeepId = selectedKeepIds[groupKey] || group[0].composer_id;
      
      return (
        <div key={groupIndex} className="duplicate-group mb-4">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <input
                  type="checkbox"
                  className="form-check-input me-3"
                  checked={selectedGroups.has(groupKey)}
                  onChange={(e) => {
                    const newSelected = new Set(selectedGroups);
                    if (e.target.checked) {
                      newSelected.add(groupKey);
                    } else {
                      newSelected.delete(groupKey);
                    }
                    setSelectedGroups(newSelected);
                  }}
                />
                <h6 className="mb-0">
                  <AlertTriangle size={16} className="me-2 text-warning" />
                  Potential Duplicates ({group.length} composers)
                </h6>
              </div>
              <button 
                className="btn btn-sm btn-primary"
                onClick={() => {
                  const toDelete = group.filter(c => c.composer_id !== selectedKeepId);
                  if (toDelete.length > 0 && window.confirm(`This will merge ${toDelete.length} duplicate(s) into the selected composer. Continue?`)) {
                    toDelete.forEach(composer => {
                      handleMergeComposers(selectedKeepId, composer.composer_id);
                    });
                  }
                }}
                disabled={merging !== null}
              >
                {merging?.startsWith('composer-') ? 'Merging...' : 'Merge Selected'}
              </button>
            </div>
            <div className="card-body">
              {group.map((composer, composerIndex) => (
                <div key={composer.composer_id} className="duplicate-item">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <input
                        type="radio"
                        name={groupKey}
                        value={composer.composer_id}
                        checked={selectedKeepId === composer.composer_id}
                        onChange={() => setSelectedKeepIds(prev => ({ ...prev, [groupKey]: composer.composer_id }))}
                        className="me-3"
                      />
                      <div>
                        <strong>{composer.full_name}</strong>
                        <div className="text-muted small">
                          ID: {composer.composer_id} | 
                          Era: {composer.era || 'Unknown'} | 
                          Origin: {composer.origin || 'Unknown'} |
                          Songs: {composer.song_count || 0}
                        </div>
                      </div>
                    </div>
                  </div>
                  {composerIndex < group.length - 1 && <hr />}
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    });
  };

  const renderTuneDuplicates = (tuneGroups: any[][]) => {
    if (tuneGroups.length === 0) {
      return <div className="text-muted text-center py-4">No tune duplicates found</div>;
    }

    return tuneGroups.map((group, groupIndex) => {
      const groupKey = `tune-group-${groupIndex}`;
      const selectedKeepId = selectedKeepIds[groupKey] || group[0].tune_id;
      
      return (
        <div key={groupIndex} className="duplicate-group mb-4">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <input
                  type="checkbox"
                  className="form-check-input me-3"
                  checked={selectedGroups.has(groupKey)}
                  onChange={(e) => {
                    const newSelected = new Set(selectedGroups);
                    if (e.target.checked) {
                      newSelected.add(groupKey);
                    } else {
                      newSelected.delete(groupKey);
                    }
                    setSelectedGroups(newSelected);
                  }}
                />
                <h6 className="mb-0">
                  <AlertTriangle size={16} className="me-2 text-warning" />
                  Potential Duplicates ({group.length} tunes)
                </h6>
              </div>
              <button 
                className="btn btn-sm btn-primary"
                onClick={() => {
                  const toDelete = group.filter(t => t.tune_id !== selectedKeepId);
                  if (toDelete.length > 0 && window.confirm(`This will merge ${toDelete.length} duplicate(s) into the selected tune. Continue?`)) {
                    toDelete.forEach(tune => {
                      handleMergeTunes(selectedKeepId, tune.tune_id);
                    });
                  }
                }}
                disabled={merging !== null}
              >
                {merging?.startsWith('tune-') ? 'Merging...' : 'Merge Selected'}
              </button>
            </div>
            <div className="card-body">
              {group.map((tune, tuneIndex) => (
                <div key={tune.tune_id} className="duplicate-item">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <input
                        type="radio"
                        name={groupKey}
                        value={tune.tune_id}
                        checked={selectedKeepId === tune.tune_id}
                        onChange={() => setSelectedKeepIds(prev => ({ ...prev, [groupKey]: tune.tune_id }))}
                        className="me-3"
                      />
                      <div>
                        <strong>{tune.tune_name}</strong>
                        <div className="text-muted small">
                          ID: {tune.tune_id} | 
                          Meter ID: {tune.meter_id || 'None'} | 
                          Preferred: {tune.is_preferred ? 'Yes' : 'No'} |
                          Songs: {tune.song_count || 0}
                        </div>
                      </div>
                    </div>
                  </div>
                  {tuneIndex < group.length - 1 && <hr />}
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    });
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          <AlertTriangle size={20} className="me-2" />
          {error}
        </div>
        <Link to="/settings" className="btn btn-secondary">
          <ArrowLeft size={16} className="me-2" />
          Back to Settings
        </Link>
      </div>
    );
  }

  return (
    <>
      <Navigation />
      <div className="container mt-4">
      {/* Header */}
      <div className="d-flex align-items-center mb-4">
        <Link to="/settings" className="btn btn-outline-secondary me-3">
          <ArrowLeft size={16} />
        </Link>
        <Database className="me-2" size={32} />
        <h1>Remove Duplicates</h1>
      </div>

      {/* Summary */}
      {duplicates && (
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <FileText size={24} className="text-primary mb-2" />
                <h5>{duplicates.summary.totalSongDuplicates}</h5>
                <small className="text-muted">Song Groups</small>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <Users size={24} className="text-info mb-2" />
                <h5>{duplicates.summary.totalComposerDuplicates}</h5>
                <small className="text-muted">Composer Groups</small>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <Music size={24} className="text-success mb-2" />
                <h5>{duplicates.summary.totalTuneDuplicates}</h5>
                <small className="text-muted">Tune Groups</small>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <AlertTriangle size={24} className="text-warning mb-2" />
                <h5>{duplicates.summary.totalDuplicates}</h5>
                <small className="text-muted">Total Groups</small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center gap-3">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => {
              if (activeTab === 'songs' && duplicates?.songs) {
                const allSongGroups = new Set<string>();
                duplicates.songs.forEach((_, index) => {
                  allSongGroups.add(`song-group-${index}`);
                });
                setSelectedGroups(allSongGroups);
              } else if (activeTab === 'composers' && duplicates?.composers) {
                const allComposerGroups = new Set<string>();
                duplicates.composers.forEach((_, index) => {
                  allComposerGroups.add(`composer-group-${index}`);
                });
                setSelectedGroups(allComposerGroups);
              } else if (activeTab === 'tunes' && duplicates?.tunes) {
                const allTuneGroups = new Set<string>();
                duplicates.tunes.forEach((_, index) => {
                  allTuneGroups.add(`tune-group-${index}`);
                });
                setSelectedGroups(allTuneGroups);
              }
            }}
          >
            Select All
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setSelectedGroups(new Set())}
          >
            Clear Selection
          </button>
          {selectedGroups.size > 0 && (
            <span className="text-muted small">
              {selectedGroups.size} group(s) selected
            </span>
          )}
        </div>
        {selectedGroups.size > 0 && (
          <button
            className="btn btn-warning"
            onClick={() => setShowMergeAllModal(true)}
            disabled={mergeAllInProgress || merging !== null}
          >
            {mergeAllInProgress ? 'Merging...' : `Merge Selected (${getSelectedGroupsCount()} items)`}
          </button>
        )}
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'songs' ? 'active' : ''}`}
            onClick={() => setActiveTab('songs')}
          >
            <FileText size={16} className="me-2" />
            Songs ({duplicates?.summary.totalSongDuplicates || 0})
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'composers' ? 'active' : ''}`}
            onClick={() => setActiveTab('composers')}
          >
            <Users size={16} className="me-2" />
            Composers ({duplicates?.summary.totalComposerDuplicates || 0})
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'tunes' ? 'active' : ''}`}
            onClick={() => setActiveTab('tunes')}
          >
            <Music size={16} className="me-2" />
            Tunes ({duplicates?.summary.totalTuneDuplicates || 0})
          </button>
        </li>
      </ul>

      {/* Content */}
      <div className="tab-content">
        {activeTab === 'songs' && duplicates && renderSongDuplicates(duplicates.songs)}
        {activeTab === 'composers' && duplicates && renderComposerDuplicates(duplicates.composers)}
        {activeTab === 'tunes' && duplicates && renderTuneDuplicates(duplicates.tunes)}
      </div>

      {/* Actions */}
      <div className="d-flex justify-content-between mt-4">
        <Link to="/settings" className="btn btn-secondary">
          <ArrowLeft size={16} className="me-2" />
          Back to Settings
        </Link>
        <button className="btn btn-primary" onClick={loadDuplicates}>
          Refresh Data
        </button>
      </div>

      {/* Confirmation Modal */}
      {showMergeAllModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Merge All</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowMergeAllModal(false)}
                  disabled={mergeAllInProgress}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning">
                  <AlertTriangle size={20} className="me-2" />
                  <strong>Warning:</strong> This action cannot be undone.
                </div>
                <p>
                  You are about to merge <strong>{getSelectedGroupsCount()} duplicate items</strong> across <strong>{selectedGroups.size} groups</strong>.
                </p>
                <p>
                  The selected items will be merged into their respective "keep" records, and the duplicate entries will be deleted permanently.
                </p>
                <div className="bg-light p-3 rounded">
                  <h6>Groups to be processed:</h6>
                  <ul className="mb-0">
                    {Array.from(selectedGroups).map(groupKey => {
                      const [type, , groupIndex] = groupKey.split('-');
                      return (
                        <li key={groupKey}>
                          {type.charAt(0).toUpperCase() + type.slice(1)} group #{parseInt(groupIndex) + 1}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowMergeAllModal(false)}
                  disabled={mergeAllInProgress}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger" 
                  onClick={handleMergeAll}
                  disabled={mergeAllInProgress}
                >
                  {mergeAllInProgress ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Merging...
                    </>
                  ) : (
                    'Confirm Merge All'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default DuplicateManagementPage;